import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Plus, FileText, Paperclip, EnvelopeSimple, Trash, PencilSimple, FloppyDisk, X,
} from "@phosphor-icons/react";

const STATUS_CLS = {
  "Sent": "bg-slate-50 text-slate-700 border border-slate-200",
  "Opened": "bg-sky-50 text-sky-700 border border-sky-200",
  "Replied": "bg-emerald-50 text-emerald-700 border border-emerald-200",
  "Interview Scheduled": "bg-violet-50 text-violet-700 border border-violet-200",
};

const emptyDraft = () => ({
  candidate_id: "", to: "", cc: "", bcc: "", subject: "",
  body_html: "", attachment_doc_ids: [], template_id: null,
});

export default function ColdEmails() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const preselectCid = params.get("candidate_id") || "";

  const [cands, setCands] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [history, setHistory] = useState([]);      // sent cold emails
  const [candDocs, setCandDocs] = useState([]);    // documents for selected candidate
  const [composerOpen, setComposerOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyDraft(), candidate_id: preselectCid });
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ id: null, name: "", subject: "", body_html: "" });
  const [busy, setBusy] = useState("");

  const loadHistory = useCallback(() => {
    api.get("/cold-emails").then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/candidates").then((r) => setCands(r.data));
    api.get("/cold-email-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
    api.get("/cold-email-drafts").then((r) => setDrafts(r.data)).catch(() => setDrafts([]));
    loadHistory();
  }, [loadHistory]);

  // Load candidate documents when candidate is selected in the composer
  useEffect(() => {
    if (!form.candidate_id) { setCandDocs([]); return; }
    api.get(`/documents?candidate_id=${form.candidate_id}`).then((r) => setCandDocs(r.data)).catch(() => setCandDocs([]));
  }, [form.candidate_id]);

  const cand = useMemo(() => cands.find((c) => c.id === form.candidate_id), [cands, form.candidate_id]);
  const nameOf = (cid) => cands.find((c) => c.id === cid)?.full_name || "—";

  const applyTemplate = (t) => {
    if (!t) return;
    setForm((f) => ({
      ...f, template_id: t.id,
      subject: t.subject || f.subject,
      body_html: t.body_html || f.body_html,
    }));
    toast.success(`Template "${t.name}" applied`);
  };

  const toggleAttachment = (docId) => {
    setForm((f) => ({
      ...f,
      attachment_doc_ids: f.attachment_doc_ids.includes(docId)
        ? f.attachment_doc_ids.filter((x) => x !== docId)
        : [...f.attachment_doc_ids, docId],
    }));
  };

  const persistDraft = async () => {
    if (!form.candidate_id || !form.to || !form.subject) {
      toast.error("Candidate, To, and Subject are required");
      return null;
    }
    setBusy("save");
    try {
      const payload = { ...form };
      let r;
      if (editingDraftId) r = await api.put(`/cold-email-drafts/${editingDraftId}`, payload);
      else r = await api.post("/cold-email-drafts", payload);
      const d = r.data;
      setEditingDraftId(d.id);
      const list = await api.get("/cold-email-drafts");
      setDrafts(list.data);
      toast.success("Draft saved");
      return d;
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); return null; }
    finally { setBusy(""); }
  };

  const sendDraft = async () => {
    const d = await persistDraft();
    if (!d) return;
    setBusy("send");
    try {
      await api.post(`/cold-email-drafts/${d.id}/send`);
      toast.success("Email sent");
      setComposerOpen(false);
      setForm({ ...emptyDraft(), candidate_id: preselectCid });
      setEditingDraftId(null);
      loadHistory();
      const list = await api.get("/cold-email-drafts"); setDrafts(list.data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const openComposer = (draft = null) => {
    if (draft) {
      setForm({
        candidate_id: draft.candidate_id || "", to: draft.to || "",
        cc: draft.cc || "", bcc: draft.bcc || "", subject: draft.subject || "",
        body_html: draft.body_html || "", attachment_doc_ids: draft.attachment_doc_ids || [],
        template_id: draft.template_id || null,
      });
      setEditingDraftId(draft.id);
    } else {
      setForm({ ...emptyDraft(), candidate_id: preselectCid });
      setEditingDraftId(null);
    }
    setComposerOpen(true);
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    try {
      await api.delete(`/cold-email-drafts/${id}`);
      const list = await api.get("/cold-email-drafts"); setDrafts(list.data);
      toast.success("Draft deleted");
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    try {
      if (tplForm.id) await api.put(`/cold-email-templates/${tplForm.id}`, { name: tplForm.name, subject: tplForm.subject, body_html: tplForm.body_html });
      else await api.post("/cold-email-templates", { name: tplForm.name, subject: tplForm.subject, body_html: tplForm.body_html });
      const list = await api.get("/cold-email-templates"); setTemplates(list.data);
      toast.success("Template saved");
      setTemplateOpen(false); setTplForm({ id: null, name: "", subject: "", body_html: "" });
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/cold-email-templates/${id}`);
      const list = await api.get("/cold-email-templates"); setTemplates(list.data);
      toast.success("Template deleted");
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // Filter history by candidate if preselected
  const shownHistory = useMemo(() => preselectCid ? history.filter((h) => h.candidate_id === preselectCid) : history, [history, preselectCid]);

  return (
    <div className="space-y-5" data-testid="cold-emails-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Cold Email Workspace</h2>
          <p className="text-sm text-slate-500">
            Compose, save drafts, use templates, attach candidate documents, and track outbound history.
            {preselectCid && cand && <> · Filtered for <b>{cand.full_name}</b> <button className="text-sky-700 hover:underline" onClick={() => nav("/cold-emails")}>(clear)</button></>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setTemplateOpen(true); setTplForm({ id: null, name: "", subject: "", body_html: "" }); }}
                  data-testid="new-template-btn"
                  className="text-sm border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 inline-flex items-center gap-1">
            <FileText size={14} /> New Template
          </button>
          <button onClick={() => openComposer(null)} data-testid="compose-email-btn"
                  className="bg-sky-600 hover:bg-sky-700 text-white text-sm px-4 py-2 rounded-md flex items-center gap-2">
            <Plus size={16} /> Compose Email
          </button>
        </div>
      </div>

      {/* Templates + Drafts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title={`Templates (${templates.length})`}>
          {templates.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No templates yet.</div>}
          {templates.map((t) => (
            <div key={t.id} className="border border-slate-100 rounded-md px-3 py-2 flex items-start justify-between gap-2 hover:bg-slate-50" data-testid={`template-${t.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{t.name}</div>
                <div className="text-xs text-slate-500 truncate">{t.subject}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setTplForm({ id: t.id, name: t.name, subject: t.subject || "", body_html: t.body_html || "" }) || setTemplateOpen(true)} title="Edit" className="text-slate-400 hover:text-slate-700"><PencilSimple size={13} /></button>
                <button onClick={() => deleteTemplate(t.id)} title="Delete" className="text-slate-400 hover:text-red-600"><Trash size={13} /></button>
              </div>
            </div>
          ))}
        </Panel>
        <Panel title={`My Drafts (${drafts.length})`}>
          {drafts.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No drafts.</div>}
          {drafts.map((d) => (
            <div key={d.id} className="border border-slate-100 rounded-md px-3 py-2 flex items-start justify-between gap-2 hover:bg-slate-50" data-testid={`draft-${d.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{d.subject || <span className="text-slate-400">(no subject)</span>}</div>
                <div className="text-xs text-slate-500 truncate">to {d.to} · {nameOf(d.candidate_id)}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openComposer(d)} title="Edit" className="text-sky-600 hover:text-sky-800"><PencilSimple size={13} /></button>
                <button onClick={() => deleteDraft(d.id)} title="Delete" className="text-slate-400 hover:text-red-600"><Trash size={13} /></button>
              </div>
            </div>
          ))}
        </Panel>
      </div>

      {/* History */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="cold-email-history">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold">Sent History {preselectCid && <span className="text-xs text-slate-400">· candidate filter active</span>}</h3>
          <span className="text-xs text-slate-500">{shownHistory.length} sent</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Candidate</th>
              <th className="text-left px-4 py-3">Company / HM</th>
              <th className="text-left px-4 py-3">Subject</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {shownHistory.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(c.date).toLocaleDateString()}</td>
                <td className="px-4 py-3"><Link className="hover:underline" to={`/candidates/${c.candidate_id}`}>{nameOf(c.candidate_id)}</Link></td>
                <td className="px-4 py-3"><b>{c.company}</b><div className="text-xs text-slate-500">{c.hiring_manager} · {c.designation}</div></td>
                <td className="px-4 py-3 text-slate-700">{c.subject}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CLS[c.status] || STATUS_CLS.Sent}`}>{c.status || "Sent"}</span></td>
              </tr>
            ))}
            {shownHistory.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-10">No emails yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* ===== Composer modal ===== */}
      {composerOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4">
          <div className="bg-white border border-slate-200 rounded-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl" data-testid="compose-email-modal">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h3 className="font-semibold">{editingDraftId ? "Edit draft" : "New email"}</h3>
              <button onClick={() => setComposerOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-3 border-b border-slate-100">
              <label className="text-xs text-slate-500 space-y-1 md:col-span-1">
                <span>Candidate</span>
                <select required value={form.candidate_id} onChange={(e) => setForm((f) => ({ ...f, candidate_id: e.target.value }))}
                        data-testid="composer-candidate"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="">— pick candidate —</option>
                  {cands.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                </select>
              </label>
              <label className="text-xs text-slate-500 space-y-1 md:col-span-2">
                <span>Template</span>
                <select onChange={(e) => applyTemplate(templates.find((t) => t.id === e.target.value))} data-testid="composer-template"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="">— optional: pick a template —</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
              <label className="text-xs text-slate-500 space-y-1 md:col-span-3">
                <span>To (comma-separated hiring managers)</span>
                <input required type="text" value={form.to} onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))}
                       data-testid="composer-to" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </label>
              <label className="text-xs text-slate-500 space-y-1">
                <span>CC</span>
                <input type="text" value={form.cc} onChange={(e) => setForm((f) => ({ ...f, cc: e.target.value }))}
                       data-testid="composer-cc" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </label>
              <label className="text-xs text-slate-500 space-y-1">
                <span>BCC</span>
                <input type="text" value={form.bcc} onChange={(e) => setForm((f) => ({ ...f, bcc: e.target.value }))}
                       data-testid="composer-bcc" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </label>
              <label className="text-xs text-slate-500 space-y-1 md:col-span-3">
                <span>Subject</span>
                <input required type="text" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                       data-testid="composer-subject" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </label>
            </div>

            <div className="px-6 py-3">
              <RichTextEditor value={form.body_html} onChange={(v) => setForm((f) => ({ ...f, body_html: v }))}
                              placeholder="Introduce the candidate, highlight strengths, share portfolio links…"
                              testId="composer-body" />
            </div>

            <div className="px-6 pb-4">
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
                <Paperclip size={11} /> Attachments from candidate's document repository
              </div>
              {!form.candidate_id ? (
                <div className="text-xs text-slate-400">Pick a candidate to see their available documents.</div>
              ) : candDocs.length === 0 ? (
                <div className="text-xs text-slate-400">No documents uploaded for this candidate.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {candDocs.map((d) => {
                    const active = form.attachment_doc_ids.includes(d.id);
                    return (
                      <button key={d.id} type="button" onClick={() => toggleAttachment(d.id)}
                              data-testid={`attach-${d.id}`}
                              className={`text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${active ? "bg-sky-600 text-white border-sky-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                        <FileText size={11} /> {d.original_filename}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 px-6 py-3 flex items-center justify-end gap-2">
              <button onClick={() => setComposerOpen(false)} className="text-sm px-4 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={persistDraft} disabled={!!busy} data-testid="composer-save-draft"
                      className="text-sm px-4 py-2 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
                <FloppyDisk size={14} /> {busy === "save" ? "Saving…" : "Save draft"}
              </button>
              <button onClick={sendDraft} disabled={!!busy} data-testid="composer-send"
                      className="text-sm px-4 py-2 rounded-md bg-sky-600 hover:bg-sky-700 text-white inline-flex items-center gap-1">
                <EnvelopeSimple size={14} /> {busy === "send" ? "Sending…" : "Send email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Template modal ===== */}
      {templateOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4">
          <form onSubmit={saveTemplate} className="bg-white border border-slate-200 rounded-xl w-full max-w-2xl p-6 space-y-3" data-testid="template-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold">{tplForm.id ? "Edit template" : "New template"}</h3>
              <button type="button" onClick={() => setTemplateOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <input required placeholder="Template name" value={tplForm.name} onChange={(e) => setTplForm((f) => ({ ...f, name: e.target.value }))}
                   data-testid="template-name" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            <input placeholder="Default subject" value={tplForm.subject} onChange={(e) => setTplForm((f) => ({ ...f, subject: e.target.value }))}
                   data-testid="template-subject" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            <RichTextEditor value={tplForm.body_html} onChange={(v) => setTplForm((f) => ({ ...f, body_html: v }))} testId="template-body" placeholder="Template body — supports formatting" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setTemplateOpen(false)} className="text-sm px-4 py-2 rounded-md border border-slate-200">Cancel</button>
              <button type="submit" data-testid="template-save" className="text-sm px-4 py-2 rounded-md bg-sky-600 hover:bg-sky-700 text-white">Save template</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <h3 className="font-semibold mb-2">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
